import { useEffect, useState } from "react";
import DropdownFormField from "./DropdownFormField";
import { fetchCodeBlocks, CodeBlock } from "../utils/BlueprintEditorAPIClient";
import { Button, Text, Tooltip, Typeahead } from "@merge-api/merge-javascript-shared";
import { Save } from "lucide-react";
import useBlueprintContext from "../context/useBlueprintContext";

interface Props {
  onSelect: (codeBlockName: string, version: string) => void;
  initialCodeBlockName?: string;
  initialVersion?: string;
  submitButtonText?: string;
}

const CodeBlockPicker = ({
  onSelect,
  initialCodeBlockName,
  initialVersion,
  submitButtonText = "Add code block step",
}: Props) => {
  const { codeBlockBranch } = useBlueprintContext();
  const [codeBlocks, setCodeBlocks] = useState<CodeBlock[]>([]);
  const [selectedCodeBlockName, setSelectedCodeBlockName] = useState<string | null>(
    initialCodeBlockName ?? null
  );
  const [selectedVersion, setSelectedVersion] = useState<string | null>(initialVersion ?? null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    fetchCodeBlocks({
      branch: codeBlockBranch,
      onSuccess: (data) => {
        setCodeBlocks(data);
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
      },
    });
  }, [codeBlockBranch]);

  const selectedCodeBlock = codeBlocks.find((cb) => cb.name === selectedCodeBlockName);
  const availableVersions = selectedCodeBlock?.versions || [];
  const isUnchanged =
    selectedCodeBlockName === initialCodeBlockName && selectedVersion === initialVersion;

  const submitButton = (
    <Button
      disabled={!selectedCodeBlockName || !selectedVersion || isUnchanged}
      fullWidth
      leftIcon={<Save size={16} />}
      onClick={() => {
        if (selectedCodeBlockName && selectedVersion) {
          onSelect(selectedCodeBlockName, selectedVersion);
        }
      }}
    >
      {submitButtonText}
    </Button>
  );

  return (
    <div className="flex flex-col gap-y-3">
      {isLoading ? (
        <div className="text-sm text-gray-500">Loading code blocks...</div>
      ) : (
        <>
          <div className="flex flex-col gap-y-1">
            <Text variant="h6">Code Block</Text>
            <Typeahead
              placeholder="Select a code block"
              options={codeBlocks.map((cb) => cb.name)}
              value={selectedCodeBlockName}
              onChange={(_, value) => {
                setSelectedCodeBlockName(value as string | null);
                setSelectedVersion(null);
              }}
              getOptionLabel={(option) => option as string}
            />
          </div>

          {selectedCodeBlockName && availableVersions.length > 0 && (
            <DropdownFormField
              currentValue={selectedVersion}
              onChange={(e) => {
                setSelectedVersion(e.target.value);
              }}
              title="Version"
              placeholder="Select a version"
              choices={availableVersions.map((version) => ({
                id: version,
                name: version,
              }))}
            />
          )}

          {isUnchanged ? (
            <Tooltip title="This is the code block's current name and version — there's no change to make">
              <div className="w-full">{submitButton}</div>
            </Tooltip>
          ) : (
            submitButton
          )}
        </>
      )}
    </div>
  );
};

export default CodeBlockPicker;
